import React, { useEffect, useState } from "react";
import http from "../Utils/axiosConfig";
import "./DailyRegister.css";

const fmt = (v) => v != null ? Number(v).toFixed(2) : "0.00";

// Explicit IST timezone (not the shift-and-cancel trick) — correct
// regardless of the machine's own local timezone setting.
const getTodayDisplay = () =>
  new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric" });

const getTodayISO = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // en-CA formats as YYYY-MM-DD

// Print via a hidden same-page iframe rather than window.open(): mobile browsers
// frequently block sized popup windows outright, and even when they don't, a
// popup's onload is unreliable and iOS Safari often refuses to print from a
// secondary window at all.
const printHtmlViaIframe = (html) => {
  let printFrame = document.getElementById("receipt-print-frame");
  if (!printFrame) {
    printFrame = document.createElement("iframe");
    printFrame.id = "receipt-print-frame";
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);
  }

  const frameDoc = printFrame.contentWindow.document;
  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  printFrame.onload = () => {
    printFrame.contentWindow.focus();
    printFrame.contentWindow.print();
  };
};

const EMPTY_SESSION = { records: [], stats: [] };

const DailyRegister = () => {
  const [morning, setMorning] = useState(EMPTY_SESSION);
  const [evening, setEvening] = useState(EMPTY_SESSION);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getTodayISO);

  const today = getTodayDisplay();
  const displayDate = selectedDate.split("-").reverse().join("/");
  const dairy_id = localStorage.getItem("temp_key");
  const dairyName = localStorage.getItem("dairy_name");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [morningRes, eveningRes] = await Promise.all([
          http.post("milk_data/get_today_data", { id: dairy_id, shift: "morning", date: selectedDate }),
          http.post("milk_data/get_today_data", { id: dairy_id, shift: "evening", date: selectedDate }),
        ]);
        setMorning(morningRes?.code === "DATA_FOUND"
          ? { records: morningRes.data.records || [], stats: morningRes.data.stats || [] }
          : EMPTY_SESSION);
        setEvening(eveningRes?.code === "DATA_FOUND"
          ? { records: eveningRes.data.records || [], stats: eveningRes.data.stats || [] }
          : EMPTY_SESSION);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dairy_id, selectedDate]);

  const sessionTotals = (stats) => {
    const buffalo = stats.find(s => s.type === "buffalo") || {};
    const cow = stats.find(s => s.type === "cow") || {};
    return {
      buffalo,
      cow,
      totalMilk: Number(buffalo.total_liters || 0) + Number(cow.total_liters || 0),
      totalRate: Number(buffalo.total_amount || 0) + Number(cow.total_amount || 0),
    };
  };

  const morningTotals = sessionTotals(morning.stats);
  const eveningTotals = sessionTotals(evening.stats);
  const dayTotalMilk = morningTotals.totalMilk + eveningTotals.totalMilk;
  const dayTotalRate = morningTotals.totalRate + eveningTotals.totalRate;
  const noData = morning.records.length === 0 && evening.records.length === 0;

  const sessionRowsHtml = (records) => records.map(r => `
      <tr>
        <td>${r.dairy_id}</td>
        <td style="text-align:left">${r.mr_name || r.en_name || ""}</td>
        <td>${fmt(r.qty)}</td>
        <td>${fmt(r.fat)}</td>
        <td>${fmt(r.snf)}</td>
        <td>${fmt(r.rate)}</td>
        <td>${fmt(r.total_rate)}</td>
      </tr>`).join("");

  const sessionSummaryHtml = (label, totals) => `
      <tr>
        <td>${label}</td>
        <td>${fmt(totals.totalMilk)} लि.</td>
        <td>₹${fmt(totals.totalRate)}</td>
      </tr>`;

  // ── Thermal print (58mm / 72mm receipt) ──
  const printThermal = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
      <style>
        @page { margin:0; size:72mm auto; }
        *{ margin:0; padding:0; box-sizing:border-box; }
        body{ font-family:'Courier New',monospace; font-size:10px; width:72mm; padding:3mm; }
        h2{ text-align:center; font-size:13px; margin-bottom:2px; }
        h3{ font-size:11px; margin:6px 0 2px; }
        .sub{ text-align:center; font-size:10px; margin-bottom:4px; border-bottom:1px dashed #000; padding-bottom:3px; }
        table{ width:100%; border-collapse:collapse; }
        th{ border-top:1px dashed #000; border-bottom:1px dashed #000; padding:2px 1px; text-align:center; font-size:9px; }
        td{ padding:2px 1px; text-align:center; font-size:10px; }
        .summary{ border-top:1px dashed #000; margin-top:4px; padding-top:4px; font-size:10px; }
        .summary tr td:first-child{ text-align:left; font-weight:bold; }
        .footer{ text-align:center; margin-top:6px; border-top:1px dashed #000; padding-top:3px; font-size:9px; }
      </style></head><body>
      <h2>${dairyName}</h2>
      <div class="sub">${displayDate} | पूर्ण दिवस</div>

      <h3>सकाळ (एकूण: ${morning.records.length})</h3>
      <table>
        <thead><tr><th>क्र.</th><th>नाव</th><th>लि.</th><th>फॅट</th><th>SNF</th><th>दर</th><th>रक्कम</th></tr></thead>
        <tbody>${sessionRowsHtml(morning.records)}</tbody>
      </table>

      <h3>संध्याकाळ (एकूण: ${evening.records.length})</h3>
      <table>
        <thead><tr><th>क्र.</th><th>नाव</th><th>लि.</th><th>फॅट</th><th>SNF</th><th>दर</th><th>रक्कम</th></tr></thead>
        <tbody>${sessionRowsHtml(evening.records)}</tbody>
      </table>

      <table class="summary">
        ${sessionSummaryHtml("सकाळ", morningTotals)}
        ${sessionSummaryHtml("संध्याकाळ", eveningTotals)}
        <tr><td>दिवसाची बेरीज</td><td>${fmt(dayTotalMilk)} लि.</td><td>₹${fmt(dayTotalRate)}</td></tr>
      </table>
      <div class="footer">धन्यवाद!</div>
      </body></html>`;

    printHtmlViaIframe(html);
  };

  // ── A4 print ──
  const printA4RowsHtml = (records) => records.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.dairy_id}</td>
        <td style="text-align:left">${r.mr_name || r.en_name || ""}</td>
        <td>${fmt(r.qty)}</td>
        <td>${fmt(r.fat)}</td>
        <td>${fmt(r.snf)}</td>
        <td>${fmt(r.rate)}</td>
        <td>${fmt(r.total_rate)}</td>
      </tr>`).join("");

  const printA4 = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
      <style>
        @page { margin:15mm; size:A4; }
        body{ font-family:Arial,sans-serif; font-size:12px; color:#000; }
        h2{ text-align:center; font-size:18px; margin-bottom:4px; }
        h3{ font-size:15px; margin:18px 0 8px; }
        .sub{ text-align:center; font-size:13px; margin-bottom:16px; }
        table{ width:100%; border-collapse:collapse; margin-bottom:8px; }
        th{ background:#003366; color:white; padding:8px 6px; text-align:center; font-size:12px; }
        td{ padding:7px 6px; text-align:center; border-bottom:1px solid #ddd; font-size:12px; }
        tr:nth-child(even) td{ background:#f5f8ff; }
        .summary-table{ margin-top:20px; }
        .summary-table th{ background:#555; }
        .summary-table td{ font-weight:bold; }
        .footer{ text-align:center; margin-top:20px; font-size:11px; color:#666; }
      </style></head><body>
      <h2>${dairyName}</h2>
      <div class="sub">दैनिक नोंद — ${displayDate} (पूर्ण दिवस)</div>

      <h3>सकाळ &nbsp;|&nbsp; एकूण सभासद: ${morning.records.length}</h3>
      <table>
        <thead><tr><th>#</th><th>सभासद क्र.</th><th>नाव</th><th>दूध (लि.)</th><th>फॅट</th><th>SNF</th><th>दर/लि.</th><th>रक्कम (₹)</th></tr></thead>
        <tbody>${printA4RowsHtml(morning.records)}</tbody>
      </table>

      <h3>संध्याकाळ &nbsp;|&nbsp; एकूण सभासद: ${evening.records.length}</h3>
      <table>
        <thead><tr><th>#</th><th>सभासद क्र.</th><th>नाव</th><th>दूध (लि.)</th><th>फॅट</th><th>SNF</th><th>दर/लि.</th><th>रक्कम (₹)</th></tr></thead>
        <tbody>${printA4RowsHtml(evening.records)}</tbody>
      </table>

      <table class="summary-table">
        <thead><tr><th></th><th>सकाळ</th><th>संध्याकाळ</th><th>दिवसाची बेरीज</th></tr></thead>
        <tbody>
          <tr>
            <td>दूध (लि.)</td>
            <td>${fmt(morningTotals.totalMilk)}</td>
            <td>${fmt(eveningTotals.totalMilk)}</td>
            <td>${fmt(dayTotalMilk)}</td>
          </tr>
          <tr>
            <td>रक्कम (₹)</td>
            <td>${fmt(morningTotals.totalRate)}</td>
            <td>${fmt(eveningTotals.totalRate)}</td>
            <td>${fmt(dayTotalRate)}</td>
          </tr>
        </tbody>
      </table>
      <div class="footer">Generated on ${today}</div>
      </body></html>`;

    printHtmlViaIframe(html);
  };

  const renderSession = (label, session, totals) => (
    <>
      <div className="dr-session-title">{label} — {session.records.length} सभासद</div>

      {session.records.length === 0 ? (
        <div className="dr-no-data"><p>{label} साठी नोंद उपलब्ध नाही</p></div>
      ) : (
        <>
          <div className="dr-table-wrap">
            <table className="dr-table">
              <thead>
                <tr>
                  <th>क्र.</th>
                  <th>नाव</th>
                  <th>दूध (लि.)</th>
                  <th>फॅट</th>
                  <th>SNF</th>
                  <th>दर/लि.</th>
                  <th>रक्कम (₹)</th>
                </tr>
              </thead>
              <tbody>
                {session.records.map((r, i) => (
                  <tr key={i}>
                    <td>{r.dairy_id}</td>
                    <td className="dr-name">{r.mr_name || r.en_name}</td>
                    <td>{fmt(r.qty)}</td>
                    <td>{fmt(r.fat)}</td>
                    <td>{fmt(r.snf)}</td>
                    <td>{fmt(r.rate)}</td>
                    <td className="dr-amount">{fmt(r.total_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dr-table-wrap dr-summary-wrap">
            <table className="dr-table dr-summary-table">
              <thead>
                <tr>
                  <th></th>
                  <th>म्हैस 🐃</th>
                  <th>गाय 🐄</th>
                  <th>एकूण</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="dr-summary-label">दूध (लि.)</td>
                  <td>{fmt(totals.buffalo.total_liters)}</td>
                  <td>{fmt(totals.cow.total_liters)}</td>
                  <td className="dr-amount">{fmt(totals.totalMilk)}</td>
                </tr>
                <tr>
                  <td className="dr-summary-label">रक्कम (₹)</td>
                  <td>{fmt(totals.buffalo.total_amount)}</td>
                  <td>{fmt(totals.cow.total_amount)}</td>
                  <td className="dr-amount">{fmt(totals.totalRate)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="dr-container">
      {/* Action bar */}
      <div className="dr-action-bar">
        <button className="dr-btn dr-btn-thermal" onClick={printThermal}>
          🖨️ Thermal Print
        </button>
        <button className="dr-btn dr-btn-a4" onClick={printA4}>
          📄 A4 Print
        </button>
      </div>

      {/* Date header */}
      <div className="dr-header">
        <input
          type="date"
          className="dr-date-input"
          value={selectedDate}
          max={getTodayISO()}
          onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
        />
        <span className="dr-shift">पूर्ण दिवस</span>
      </div>

      {loading ? (
        <div className="dr-no-data"><p>लोड होत आहे...</p></div>
      ) : noData ? (
        <div className="dr-no-data"><p>या तारखेसाठी नोंद उपलब्ध नाही</p></div>
      ) : (
        <>
          {renderSession("सकाळ", morning, morningTotals)}
          {renderSession("संध्याकाळ", evening, eveningTotals)}

          <div className="dr-table-wrap dr-summary-wrap">
            <table className="dr-table dr-summary-table">
              <thead>
                <tr>
                  <th></th>
                  <th>सकाळ</th>
                  <th>संध्याकाळ</th>
                  <th>दिवसाची बेरीज</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="dr-summary-label">दूध (लि.)</td>
                  <td>{fmt(morningTotals.totalMilk)}</td>
                  <td>{fmt(eveningTotals.totalMilk)}</td>
                  <td className="dr-amount">{fmt(dayTotalMilk)}</td>
                </tr>
                <tr>
                  <td className="dr-summary-label">रक्कम (₹)</td>
                  <td>{fmt(morningTotals.totalRate)}</td>
                  <td>{fmt(eveningTotals.totalRate)}</td>
                  <td className="dr-amount">{fmt(dayTotalRate)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default DailyRegister;
